import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FolderOpen, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import {
  useApplyFolderTemplate,
  useCreateFolderTemplate,
  useDeleteFolderTemplate,
  useFolderTemplates,
  useUpdateFolderTemplate,
} from '@/api/adminTemplates';
import { useOrganizations } from '@/api/organizations';
import { useWorkspaces } from '@/api/workspaces';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { FolderTemplate, FolderTemplateNode } from '@/types/api';

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return format(d, 'PP');
}

// ---------------------------------------------------------------------------
// Parse / serialize helpers
// ---------------------------------------------------------------------------

function parseStructure(text: string): FolderTemplateNode[] {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const stack: { depth: number; nodes: FolderTemplateNode[] }[] = [
    { depth: -1, nodes: [] },
  ];
  for (const line of lines) {
    const match = /^(\s*)(.+)$/.exec(line);
    if (!match) continue;
    const depth = Math.floor(match[1].length / 2);
    const name = match[2].trim();
    while (stack.length && stack[stack.length - 1].depth >= depth) stack.pop();
    const parent = stack[stack.length - 1];
    const node: FolderTemplateNode = { name, children: [] };
    parent.nodes.push(node);
    stack.push({ depth, nodes: node.children as FolderTemplateNode[] });
  }
  return stack[0].nodes;
}

function serializeTree(nodes: FolderTemplateNode[], depth: number): string {
  return nodes
    .map(
      (n) =>
        `${'  '.repeat(depth)}${n.name}${
          n.children && n.children.length > 0
            ? '\n' + serializeTree(n.children, depth + 1)
            : ''
        }`,
    )
    .join('\n');
}

function renderTreeText(nodes: FolderTemplateNode[], depth: number): string {
  return nodes
    .map(
      (n) =>
        `${'  '.repeat(depth)}${n.name}\n${
          n.children ? renderTreeText(n.children, depth + 1) : ''
        }`,
    )
    .join('');
}

function deepCloneNodes(nodes: FolderTemplateNode[]): FolderTemplateNode[] {
  return nodes.map((n) => ({
    name: n.name,
    children: n.children ? deepCloneNodes(n.children) : [],
  }));
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function FolderTemplatesPage() {
  usePageTitle('Folder templates');
  const { data, isLoading, isError, error } = useFolderTemplates();
  const create = useCreateFolderTemplate();
  const update = useUpdateFolderTemplate();
  const del = useDeleteFolderTemplate();
  const toast = useToast();

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FolderTemplate | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [structure, setStructure] = useState(
    'Intake\n  Engagement Letter\n  W-9\nFinancials\n  Bank Statements\n  Invoices',
  );

  // Delete confirm state
  const [deleteTarget, setDeleteTarget] = useState<FolderTemplate | null>(null);

  const openNew = () => {
    setEditing(null);
    setName('');
    setDesc('');
    setStructure(
      'Intake\n  Engagement Letter\n  W-9\nFinancials\n  Bank Statements\n  Invoices',
    );
    setModalOpen(true);
  };

  const openEdit = (t: FolderTemplate) => {
    setEditing(t);
    setName(t.name);
    setDesc(t.description ?? '');
    setStructure(
      t.structure && t.structure.length > 0
        ? serializeTree(t.structure, 0)
        : '',
    );
    setModalOpen(true);
  };

  const handleSave = async () => {
    const parsed = parseStructure(structure);
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          name: name.trim(),
          description: desc.trim() || undefined,
          structure: parsed,
        });
        toast.success('Template updated');
      } else {
        await create.mutateAsync({
          name: name.trim(),
          description: desc.trim() || undefined,
          structure: parsed,
        });
        toast.success('Template created');
      }
      setModalOpen(false);
      setEditing(null);
    } catch (e) {
      toast.error(editing ? 'Update failed' : 'Create failed', apiError(e));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.mutateAsync(deleteTarget.id);
      toast.success(`Deleted "${deleteTarget.name}"`);
      setDeleteTarget(null);
    } catch (e) {
      toast.error('Delete failed', apiError(e));
    }
  };

  return (
    <>
      <Card
        title="Folder templates"
        actions={
          <Button variant="secondary" onClick={openNew} className="text-xs">
            <Plus size={14} /> New template
          </Button>
        }
      >
        <p className="text-xs text-slate-500 mb-3">
          Folder templates pre-populate new client workspaces. Apply a template to
          an existing workspace to add any missing folders without removing existing
          ones.
        </p>
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <div className="text-sm text-red-600">{apiError(error)}</div>
        ) : (data?.length ?? 0) === 0 ? (
          <EmptyState
            title="No templates"
            description="Create a folder template to apply a consistent structure across client workspaces."
            icon={<Plus size={24} />}
            action={
              <Button variant="secondary" onClick={openNew}>
                <Plus size={14} /> New template
              </Button>
            }
          />
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(data ?? []).map((t) => (
              <li key={t.id} className="border border-slate-200 p-4 bg-white">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <button
                      type="button"
                      onClick={() => openEdit(t)}
                      className="text-sm font-semibold text-bb-navy hover:underline text-left truncate block"
                    >
                      {t.name}
                    </button>
                    {t.description && (
                      <div className="text-xs text-slate-500 mt-0.5">
                        {t.description}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <span className="text-xs text-slate-400 whitespace-nowrap mr-1">
                      {formatDate(t.updated_at)}
                    </span>
                    <Button
                      variant="secondary"
                      className="text-xs"
                      onClick={() => openEdit(t)}
                    >
                      <Pencil size={12} />
                    </Button>
                    <Button
                      variant="secondary"
                      className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                      onClick={() => setDeleteTarget(t)}
                    >
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </div>
                <pre className="text-xs bg-slate-50 p-2 mt-2 max-h-40 overflow-auto custom-scrollbar whitespace-pre-wrap">
                  {t.structure && t.structure.length > 0 ? (
                    renderTreeText(t.structure, 0)
                  ) : (
                    <span className="text-slate-400">(empty)</span>
                  )}
                </pre>
                <ApplyTemplate templateId={t.id} />
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Create / Edit Modal */}
      <TemplateEditorModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        editing={!!editing}
        name={name}
        setName={setName}
        desc={desc}
        setDesc={setDesc}
        structure={structure}
        setStructure={setStructure}
        onSave={handleSave}
        saving={create.isPending || update.isPending}
      />

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={
          deleteTarget
            ? `Delete "${deleteTarget.name}"?`
            : 'Delete template?'
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={del.isPending}
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-700">
          This template will be permanently removed. Existing workspaces that
          were created from it are unaffected.
        </p>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Editable tree node (visual editor)
// ---------------------------------------------------------------------------

interface EditableTreeNodeProps {
  node: FolderTemplateNode;
  depth: number;
  onChangeName: (newName: string) => void;
  onAddChild: () => void;
  onDelete: () => void;
  onChangeChildren: (children: FolderTemplateNode[]) => void;
}

function EditableTreeNode({
  node,
  depth,
  onChangeName,
  onAddChild,
  onDelete,
  onChangeChildren,
}: EditableTreeNodeProps) {
  const children = node.children ?? [];

  const handleChildName = (idx: number, newName: string) => {
    const updated = deepCloneNodes(children);
    updated[idx].name = newName;
    onChangeChildren(updated);
  };

  const handleChildAddChild = (idx: number) => {
    const updated = deepCloneNodes(children);
    if (!updated[idx].children) updated[idx].children = [];
    updated[idx].children!.push({ name: '', children: [] });
    onChangeChildren(updated);
  };

  const handleChildDelete = (idx: number) => {
    const updated = deepCloneNodes(children);
    updated.splice(idx, 1);
    onChangeChildren(updated);
  };

  const handleChildChangeChildren = (
    idx: number,
    newChildren: FolderTemplateNode[],
  ) => {
    const updated = deepCloneNodes(children);
    updated[idx].children = newChildren;
    onChangeChildren(updated);
  };

  return (
    <div>
      <div
        className="flex items-center gap-1 py-0.5 group"
        style={{ paddingLeft: `${depth * 16}px` }}
      >
        <FolderOpen size={13} className="text-bb-navy/60 shrink-0" />
        <input
          type="text"
          value={node.name}
          onChange={(e) => onChangeName(e.target.value)}
          placeholder="(folder name)"
          className="flex-1 min-w-0 text-xs border border-transparent hover:border-bb-lilac focus:border-bb-navy focus:ring-1 focus:ring-bb-navy/30 bg-transparent px-1 py-0.5 focus:outline-none"
        />
        <button
          type="button"
          onClick={onAddChild}
          className="text-xs text-slate-400 hover:text-bb-navy p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Add child folder"
        >
          <Plus size={12} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-slate-400 hover:text-red-600 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove folder"
        >
          <X size={12} />
        </button>
      </div>
      {children.map((child, i) => (
        <EditableTreeNode
          key={`${depth + 1}-${i}`}
          node={child}
          depth={depth + 1}
          onChangeName={(n) => handleChildName(i, n)}
          onAddChild={() => handleChildAddChild(i)}
          onDelete={() => handleChildDelete(i)}
          onChangeChildren={(c) => handleChildChangeChildren(i, c)}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Interactive tree preview (two-way sync)
// ---------------------------------------------------------------------------

function EditableTreePreview({
  structure,
  setStructure,
}: {
  structure: string;
  setStructure: (v: string) => void;
}) {
  const parsed = useMemo(() => parseStructure(structure), [structure]);

  const syncBack = useCallback(
    (nodes: FolderTemplateNode[]) => {
      setStructure(serializeTree(nodes, 0));
    },
    [setStructure],
  );

  const handleChangeName = (idx: number, newName: string) => {
    const updated = deepCloneNodes(parsed);
    updated[idx].name = newName;
    syncBack(updated);
  };

  const handleAddChild = (idx: number) => {
    const updated = deepCloneNodes(parsed);
    if (!updated[idx].children) updated[idx].children = [];
    updated[idx].children!.push({ name: '', children: [] });
    syncBack(updated);
  };

  const handleDelete = (idx: number) => {
    const updated = deepCloneNodes(parsed);
    updated.splice(idx, 1);
    syncBack(updated);
  };

  const handleChangeChildren = (
    idx: number,
    children: FolderTemplateNode[],
  ) => {
    const updated = deepCloneNodes(parsed);
    updated[idx].children = children;
    syncBack(updated);
  };

  const handleAddRoot = () => {
    const updated = deepCloneNodes(parsed);
    updated.push({ name: '', children: [] });
    syncBack(updated);
  };

  if (parsed.length === 0) {
    return (
      <div className="border border-slate-200 bg-slate-50 p-3">
        <div className="text-xs text-slate-400 italic mb-2">
          No folders yet. Add one to get started.
        </div>
        <Button
          variant="secondary"
          onClick={handleAddRoot}
          className="text-xs"
        >
          <Plus size={12} /> Add root folder
        </Button>
      </div>
    );
  }

  return (
    <div className="border border-slate-200 bg-slate-50 p-3 max-h-64 overflow-auto custom-scrollbar">
      {parsed.map((node, i) => (
        <EditableTreeNode
          key={`0-${i}`}
          node={node}
          depth={0}
          onChangeName={(n) => handleChangeName(i, n)}
          onAddChild={() => handleAddChild(i)}
          onDelete={() => handleDelete(i)}
          onChangeChildren={(c) => handleChangeChildren(i, c)}
        />
      ))}
      <button
        type="button"
        onClick={handleAddRoot}
        className="mt-2 flex items-center gap-1 text-xs text-slate-400 hover:text-bb-navy"
      >
        <Plus size={12} /> Add root folder
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-folder helper row
// ---------------------------------------------------------------------------

function AddFolderRow({ onAdd }: { onAdd: (name: string, depth: number) => void }) {
  const [folderName, setFolderName] = useState('');
  const [depth, setDepth] = useState(0);

  const handleAdd = () => {
    const trimmed = folderName.trim();
    if (!trimmed) return;
    onAdd(trimmed, depth);
    setFolderName('');
  };

  return (
    <div className="flex items-center gap-2">
      <Input
        value={folderName}
        onChange={(e) => setFolderName(e.target.value)}
        placeholder="Folder name"
        className="flex-1 text-xs"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
          }
        }}
      />
      <Select
        value={String(depth)}
        onChange={(v) => setDepth(Number(v))}
        options={[
          { value: '0', label: 'Root' },
          { value: '1', label: 'Child (1 level)' },
          { value: '2', label: 'Grandchild (2 levels)' },
          { value: '3', label: '3 levels deep' },
        ]}
        placeholder=""
        className="text-xs"
      />
      <Button
        variant="secondary"
        onClick={handleAdd}
        disabled={!folderName.trim()}
        className="text-xs shrink-0"
      >
        Add
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Template editor modal (create + edit)
// ---------------------------------------------------------------------------

function TemplateEditorModal({
  open,
  onClose,
  editing,
  name,
  setName,
  desc,
  setDesc,
  structure,
  setStructure,
  onSave,
  saving,
}: {
  open: boolean;
  onClose: () => void;
  editing: boolean;
  name: string;
  setName: (v: string) => void;
  desc: string;
  setDesc: (v: string) => void;
  structure: string;
  setStructure: (v: string) => void;
  onSave: () => void;
  saving: boolean;
}) {
  const handleAddFolder = (folderName: string, depth: number) => {
    const indent = '  '.repeat(depth);
    const line = `${indent}${folderName}`;
    const trimmed = structure.trimEnd();
    setStructure(trimmed ? `${trimmed}\n${line}` : line);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Edit folder template' : 'New folder template'}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onSave}
            loading={saving}
            disabled={!name.trim()}
            className="w-auto"
          >
            {editing ? 'Save changes' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label htmlFor="t-name">Name</Label>
          <Input id="t-name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="t-desc">Description</Label>
          <Input id="t-desc" value={desc} onChange={(e) => setDesc(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="t-struct">Structure</Label>
          <p className="text-xs text-slate-500 mb-1">
            Use 2-space indentation to create sub-folders. Each indentation level
            nests one level deeper.
          </p>
          <textarea
            id="t-struct"
            rows={8}
            value={structure}
            onChange={(e) => setStructure(e.target.value)}
            className="w-full font-mono border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bb-navy/30 focus:border-bb-navy"
          />
        </div>
        <div>
          <Label>Visual editor</Label>
          <p className="text-xs text-slate-500 mb-1">
            Click a folder name to rename it. Use the + and x buttons to add children or delete nodes.
          </p>
          <EditableTreePreview structure={structure} setStructure={setStructure} />
        </div>
        <div>
          <Label>Quick add</Label>
          <AddFolderRow onAdd={handleAddFolder} />
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Apply template to workspace
// ---------------------------------------------------------------------------

function ApplyTemplate({ templateId }: { templateId: string }) {
  const orgs = useOrganizations();
  const [orgId, setOrgId] = useState('');
  const workspaces = useWorkspaces(orgId || null);
  const apply = useApplyFolderTemplate();
  const toast = useToast();
  const workspaceId = workspaces.data?.[0]?.id;

  const doApply = async () => {
    if (!orgId) {
      toast.error('Pick an organization first');
      return;
    }
    if (workspaces.isLoading) {
      toast.info('Loading workspace, try again in a moment');
      return;
    }
    if (!workspaceId) {
      toast.error('No workspace found for this organization');
      return;
    }
    try {
      const result = await apply.mutateAsync({ templateId, workspaceId });
      const created = result?.folders_created ?? 0;
      toast.success(
        created > 0
          ? `Created ${created} folder${created === 1 ? '' : 's'}`
          : 'Template already applied — no new folders to create',
      );
    } catch (e) {
      toast.error('Apply failed', apiError(e));
    }
  };

  return (
    <div className="mt-3 space-y-2">
      <div className="flex items-center gap-2">
        <Select
          value={orgId}
          onChange={setOrgId}
          options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
          placeholder="Apply to..."
          className="flex-1 text-xs"
        />
        <Button
          variant="secondary"
          onClick={doApply}
          disabled={apply.isPending}
          loading={apply.isPending}
          className="text-xs"
        >
          Apply
        </Button>
      </div>
      {orgId && workspaces.isLoading && (
        <p className="text-[11px] text-slate-500">Loading workspace...</p>
      )}
      {orgId && !workspaces.isLoading && !workspaceId && (
        <p className="text-[11px] text-red-600">
          No workspace exists for this organization yet.
        </p>
      )}
      {orgId && workspaceId && (
        <p className="text-[11px] text-slate-500">
          Will create any missing folders in this workspace.{' '}
          <Link
            to={`/workspace/${orgId}`}
            className="text-bb-navy hover:underline"
          >
            View workspace →
          </Link>
        </p>
      )}
    </div>
  );
}
